import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;
import { 
  Shirt, 
  Heart, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  Sparkles,
  ChevronRight,
  Upload,
  X,
  Loader2,
  MessageSquare,
  Shield,
  Zap,
  ArrowRight,
  Ruler,
  Scissors,
  Palette,
  User,
  Briefcase,
  Calendar,
  Award
} from 'lucide-react';
import { toast } from 'sonner';

// Shirt types
const SHIRT_TYPES = [
  { value: 'classica', label: 'Camicia classica / Business', icon: Briefcase },
  { value: 'casual', label: 'Camicia casual / Sportiva', icon: User },
  { value: 'cerimonia', label: 'Camicia da cerimonia', icon: Award },
  { value: 'smoking', label: 'Camicia da smoking', icon: Star },
  { value: 'lino', label: 'Camicia estiva / Lino', icon: Sparkles },
  { value: 'flanella', label: 'Camicia in flanella', icon: Heart },
  { value: 'jeans', label: 'Camicia in jeans/denim', icon: Shirt },
  { value: 'hawaiana', label: 'Camicia fantasia / Hawaiana', icon: Palette },
  { value: 'su_misura', label: 'Camicia su misura sartoriale', icon: Scissors },
  { value: 'altro', label: 'Altro', icon: Shirt },
];

// Fabric types
const FABRICS = [
  { value: 'cotone', label: 'Cotone 100%', icon: Shirt },
  { value: 'popeline', label: 'Popeline di cotone', icon: Sparkles },
  { value: 'oxford', label: 'Oxford', icon: Briefcase },
  { value: 'twill', label: 'Twill / Diagonale', icon: Shirt },
  { value: 'lino', label: 'Lino puro', icon: Heart },
  { value: 'lino_cotone', label: 'Misto lino-cotone', icon: Shirt },
  { value: 'seta', label: 'Seta', icon: Star },
  { value: 'flanella', label: 'Flanella di cotone', icon: Heart },
  { value: 'chambray', label: 'Chambray', icon: Palette },
  { value: 'denim', label: 'Denim / Jeans', icon: Shirt },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Sparkles },
];

// Collar types
const COLLAR_TYPES = [
  { value: 'italiano', label: 'Collo italiano (classico)', icon: Shirt },
  { value: 'francese', label: 'Collo francese (diffuso)', icon: Sparkles },
  { value: 'button_down', label: 'Button-down', icon: Briefcase },
  { value: 'coreano', label: 'Collo coreano / Alla coreana', icon: Star },
  { value: 'diplomatico', label: 'Collo diplomatico (per gemelli)', icon: Award },
  { value: 'smoking', label: 'Collo da smoking (diplomatico rigido)', icon: Star },
  { value: 'cutaway', label: 'Cutaway / Shark', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Heart },
];

// Cuff types
const CUFF_TYPES = [
  { value: 'singolo', label: 'Polsino singolo (bottone)', icon: Shirt },
  { value: 'doppio', label: 'Polsino doppio (gemelli)', icon: Star },
  { value: 'arrotondato', label: 'Polsino arrotondato', icon: Sparkles },
  { value: 'non_so', label: 'Non so, vorrei consiglio', icon: Heart },
];

// Occasion
const OCCASIONS = [
  { value: 'lavoro', label: 'Lavoro / Ufficio', icon: Briefcase },
  { value: 'cerimonia', label: 'Cerimonia / Matrimonio', icon: Award },
  { value: 'casual', label: 'Casual / Tempo libero', icon: User },
  { value: 'serata', label: 'Serata elegante', icon: Star },
  { value: 'quotidiano', label: 'Uso quotidiano', icon: Calendar },
  { value: 'regalo', label: 'Regalo', icon: Heart },
  { value: 'altro', label: 'Altro', icon: Shirt },
];

// Budget options
const BUDGET_OPTIONS = [
  { value: '50-100', label: '€50 - €100', description: 'Camicia artigianale base' },
  { value: '100-150', label: '€100 - €150', description: 'Tessuti pregiati' },
  { value: '150-250', label: '€150 - €250', description: 'Su misura sartoriale' },
  { value: '250-400', label: '€250 - €400', description: 'Alta sartoria' },
  { value: '400+', label: '€400+', description: 'Lusso / Seta pura' },
];

// Timeline options
const TIMELINE_OPTIONS = [
  { value: '1week', label: '1 Settimana', description: 'Urgente' },
  { value: '2weeks', label: '2 Settimane', description: 'Prioritario' },
  { value: '3weeks', label: '3 Settimane', description: 'Standard' },
  { value: '1month', label: '1 Mese', description: 'Con calma' },
  { value: 'flexible', label: 'Flessibile', description: 'Nessuna urgenza' },
];

// Example shirts
const SHIRT_EXAMPLES = [
  {
    idea: "Camicia elegante per il mio matrimonio",
    result: "Camicia in popeline bianco con collo diplomatico e polsini doppi per gemelli, bottoni madreperla",
    image: "https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=400&h=300&fit=crop",
    category: "Cerimonia"
  },
  {
    idea: "Camicia da ufficio personalizzata con le mie iniziali",
    result: "Camicia in twill azzurro con monogramma ricamato sul polsino, collo italiano",
    image: "https://images.unsplash.com/photo-1598033129183-c4f50c736f10?w=400&h=300&fit=crop",
    category: "Business"
  },
  {
    idea: "Camicia estiva in lino per le vacanze",
    result: "Camicia in lino naturale con collo coreano, vestibilità rilassata, colore sabbia",
    image: "https://images.unsplash.com/photo-1596755094514-f87e34085b2c?w=400&h=300&fit=crop",
    category: "Casual"
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: "La camicia su misura che ho fatto realizzare veste perfettamente. Il sarto ha preso le misure con precisione millimetrica.",
    author: "Marco R.",
    location: "Milano"
  },
  {
    text: "Per il matrimonio di mio figlio volevo una camicia speciale. Il risultato è stato oltre le aspettative, tessuto e rifiniture impeccabili.",
    author: "Giuseppe L.",
    location: "Roma"
  },
  {
    text: "Finalmente camicie che vestono bene! Ho ordinato 3 camicie per l'ufficio, tutte perfette. Non torno più al prêt-à-porter.",
    author: "Andrea B.",
    location: "Firenze"
  }
];

// FAQ items
const FAQ_ITEMS = [
  {
    question: "Come vengono prese le misure per una camicia su misura?",
    answer: "Il sarto ti guiderà nel prendere le misure essenziali: collo, spalle, petto, vita, lunghezza braccio e lunghezza totale. Puoi anche inviare una camicia che ti veste bene come riferimento."
  },
  {
    question: "Quanto tempo ci vuole per realizzare una camicia?",
    answer: "Una camicia artigianale richiede generalmente 2-3 settimane. Per camicie da cerimonia o con dettagli particolari, calcola 3-4 settimane."
  },
  {
    question: "Posso scegliere tutti i dettagli della camicia?",
    answer: "Assolutamente sì! Puoi personalizzare tessuto, colore, tipo di collo, polsino, bottoni, tasca, e aggiungere monogrammi o dettagli su richiesta."
  },
  {
    question: "Cosa succede se la camicia non veste bene?",
    answer: "I nostri sarti offrono generalmente una prova intermedia o modifiche post-consegna. Discuti le politiche di reso/modifica prima di confermare l'ordine."
  },
  {
    question: "Quali tessuti sono migliori per l'estate?",
    answer: "Per l'estate consigliamo lino, misto lino-cotone, o cotone leggero come il popeline. Evita flanella e twill pesante che sono più adatti all'inverno."
  }
];

export default function CamicieRequestPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    shirt_type: '',
    fabric: '',
    collar: '',
    cuff: '',
    occasion: '',
    budget: '',
    timeline: '',
    attachments: []
  });
  
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    document.title = 'Camicie Su Misura | Sartoria Italiana | Rarifex';
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.attachments.length + files.length > 5) {
      toast.error('Puoi caricare massimo 5 immagini');
      return;
    }
    
    setUploadingFile(true);
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini sono permesse');
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Immagine troppo grande (max 5MB)');
        continue;
      }
      
      try {
        const fileName = `shirt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const filePath = `requests/${fileName}`;
        
        const { error } = await supabase.storage
          .from('rarifex-uploads')
          .upload(filePath, file);
        
        if (error) throw error;
        
        const { data: { publicUrl } } = supabase.storage
          .from('rarifex-uploads')
          .getPublicUrl(filePath);
        
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, publicUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Errore nel caricamento');
      }
    }
    
    setUploadingFile(false);
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      toast.error('Descrivi la camicia che desideri');
      return;
    }
    
    if (!user) {
      setShowAuthPrompt(true);
      localStorage.setItem('rarifex_pending_shirt_request', JSON.stringify(formData));
      return;
    }
    
    setLoading(true);
    
    try {
      const shirtTypeLabel = SHIRT_TYPES.find(t => t.value === formData.shirt_type)?.label || '';
      const fabricLabel = FABRICS.find(t => t.value === formData.fabric)?.label || '';
      const collarLabel = COLLAR_TYPES.find(t => t.value === formData.collar)?.label || '';
      const cuffLabel = CUFF_TYPES.find(t => t.value === formData.cuff)?.label || '';
      const occasionLabel = OCCASIONS.find(p => p.value === formData.occasion)?.label || '';
      const budgetLabel = BUDGET_OPTIONS.find(b => b.value === formData.budget)?.label || '';
      const timelineLabel = TIMELINE_OPTIONS.find(t => t.value === formData.timeline)?.label || '';
      
      // Build formatted description with all form data
      let fullDescription = formData.description;
      
      const details = [];
      if (shirtTypeLabel) details.push(`👔  Tipo: ${shirtTypeLabel}`);
      if (fabricLabel) details.push(`🧵  Tessuto: ${fabricLabel}`);
      if (collarLabel) details.push(`✂️  Collo: ${collarLabel}`);
      if (cuffLabel) details.push(`🔘  Polsino: ${cuffLabel}`);
      if (occasionLabel) details.push(`📅  Occasione: ${occasionLabel}`);
      if (budgetLabel) details.push(`💰  Budget: ${budgetLabel}`);
      if (timelineLabel) details.push(`⏱️  Tempi: ${timelineLabel}`);
      
      if (details.length > 0) {
        const separator = '━━━━━━━━━━━━━━━━━━━━━━━';
        fullDescription += `\n\n${separator}\n✨ DETTAGLI RICHIESTA\n${separator}\n` + details.join('\n');
      }
      
      const requestData = {
        client_id: user.id,
        title: formData.title || `${shirtTypeLabel} - ${fabricLabel}`.trim() || 'Camicia su misura',
        description: fullDescription,
        category: 'Camicie',
        price_range: formData.budget || 'flexible',
        timeline: formData.timeline || 'flexible',
        attachments: formData.attachments,
      };
      
      const response = await fetch(`${API_URL}/api/requests/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la creazione');
      }
      
      toast.success('Richiesta inviata! Riceverai presto proposte dai sarti.');
      navigate('/requests');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Errore nell\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('shirt-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <Helmet>
        <title>Camicie Su Misura | Sartoria Artigianale Italiana | Rarifex</title>
        <meta name="description" content="Camicie su misura realizzate da sarti italiani. Scegli tessuto, collo, polsino e ogni dettaglio. Sartoria artigianale Made in Italy. Gratuito e senza impegno." />
        <meta name="keywords" content="camicia su misura, camicia sartoriale, camicia artigianale, sarto italiano, camicia personalizzata, camicia uomo, sartoria italiana" />
        <link rel="canonical" href="https://www.rarifex.com/camicie-su-misura" />
        <meta property="og:title" content="Camicie Su Misura | Sartoria Artigianale Italiana | Rarifex" />
        <meta property="og:description" content="Camicie su misura realizzate da sarti italiani. Gratuito e senza impegno." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rarifex.com/camicie-su-misura" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50/50 via-white to-blue-50/30">
        
        {/* HERO SECTION */}
        <section className="relative py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-100/50 via-transparent to-transparent" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Left: Hero Content */}
                <div className="text-center lg:text-left">
                  <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Shirt className="w-4 h-4" />
                    Camicie Su Misura
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                    La camicia perfetta{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-700 to-blue-600">
                      cucita su di te
                    </span>
                  </h1>
                  
                  <p className="text-xl md:text-2xl text-slate-600 mb-8 leading-relaxed">
                    Sarti italiani creano la camicia dei tuoi sogni, su misura.
                  </p>
                  
                  <p className="text-lg text-slate-500 mb-8">
                    Tessuti pregiati, rifiniture sartoriali, vestibilità perfetta.
                    <br />
                    <strong className="text-slate-700">Gratuito e senza impegno.</strong>
                  </p>
                  
                  {/* Trust badges */}
                  <div className="flex flex-wrap justify-center lg:justify-start gap-4 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span>Sarti verificati</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-500" />
                      <span>Risposte in 24-48h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-slate-500" />
                      <span>100% Made in Italy</span>
                    </div>
                  </div>
                </div>
                
                {/* Right: Form Card */}
                <div id="shirt-form" className="relative">
                  <Card className="p-6 md:p-8 shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
                    <form onSubmit={handleSubmit} className="space-y-5">
                      
                      {/* Title */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Titolo del tuo progetto
                        </Label>
                        <Input
                          placeholder="Es: Camicia elegante per matrimonio"
                          value={formData.title}
                          onChange={(e) => handleInputChange('title', e.target.value)}
                          className="mt-1.5"
                          data-testid="shirt-title-input"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Descrivi la camicia che desideri *
                        </Label>
                        <Textarea
                          placeholder="Racconta la tua idea... Colore, fantasia, dettagli particolari, misure se le conosci. Allega foto di riferimento se ne hai."
                          value={formData.description}
                          onChange={(e) => handleInputChange('description', e.target.value)}
                          className="mt-1.5 min-h-[120px]"
                          required
                          data-testid="shirt-description-textarea"
                        />
                      </div>
                      
                      {/* Type & Fabric */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tipo di camicia
                          </Label>
                          <select
                            value={formData.shirt_type}
                            onChange={(e) => handleInputChange('shirt_type', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                            data-testid="shirt-type-select"
                          >
                            <option value="">Seleziona...</option>
                            {SHIRT_TYPES.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">
                            Tessuto preferito
                          </Label>
                          <select
                            value={formData.fabric}
                            onChange={(e) => handleInputChange('fabric', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                            data-testid="shirt-fabric-select"
                          >
                            <option value="">Seleziona...</option>
                            {FABRICS.map(fab => (
                              <option key={fab.value} value={fab.value}>{fab.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Collar & Cuff */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo di collo</Label>
                          <select
                            value={formData.collar}
                            onChange={(e) => handleInputChange('collar', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                            data-testid="shirt-collar-select"
                          >
                            <option value="">Seleziona...</option>
                            {COLLAR_TYPES.map(col => (
                              <option key={col.value} value={col.value}>{col.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Tipo di polsino</Label>
                          <select
                            value={formData.cuff}
                            onChange={(e) => handleInputChange('cuff', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                            data-testid="shirt-cuff-select"
                          >
                            <option value="">Seleziona...</option>
                            {CUFF_TYPES.map(cuff => (
                              <option key={cuff.value} value={cuff.value}>{cuff.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Occasion & Budget */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Occasione</Label>
                          <select
                            value={formData.occasion}
                            onChange={(e) => handleInputChange('occasion', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                            data-testid="shirt-occasion-select"
                          >
                            <option value="">Seleziona...</option>
                            {OCCASIONS.map(occ => (
                              <option key={occ.value} value={occ.value}>{occ.label}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium text-slate-700">Budget</Label>
                          <select
                            value={formData.budget}
                            onChange={(e) => handleInputChange('budget', e.target.value)}
                            className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                            data-testid="shirt-budget-select"
                          >
                            <option value="">Seleziona...</option>
                            {BUDGET_OPTIONS.map(opt => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label} - {opt.description}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      {/* Timeline */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">Tempi di consegna</Label>
                        <select
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-500"
                          data-testid="shirt-timeline-select"
                        >
                          <option value="">Seleziona...</option>
                          {TIMELINE_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label} - {opt.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Attachments */}
                      <div>
                        <Label className="text-sm font-medium text-slate-700">
                          Foto di riferimento o ispirazioni
                        </Label>
                        <div className="mt-1.5">
                          {formData.attachments.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {formData.attachments.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(idx)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {formData.attachments.length < 5 && (
                            <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-slate-300 hover:bg-slate-50/50 transition-colors">
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploadingFile}
                                data-testid="shirt-file-upload"
                              />
                              {uploadingFile ? (
                                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                              ) : (
                                <Upload className="w-5 h-5 text-slate-400" />
                              )}
                              <span className="text-sm text-slate-500">
                                Carica foto o ispirazioni ({formData.attachments.length}/5)
                              </span>
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white shadow-lg shadow-slate-200"
                        data-testid="shirt-submit-button"
                      >
                        {loading ? (
                          <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        ) : (
                          <Shirt className="w-5 h-5 mr-2" />
                        )}
                        Ricevi proposte gratis
                      </Button>
                      
                    </form>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: PROBLEMA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                Le camicie standard non vestono mai bene
              </h2>
              
              <div className="space-y-4 text-lg text-slate-600 mb-8">
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Collo troppo largo o troppo stretto
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Maniche sempre da accorciare
                </p>
                <p className="flex items-center justify-center gap-3">
                  <span className="text-slate-400">✗</span>
                  Tessuti sintetici che non respirano
                </p>
              </div>
              
              <p className="text-xl text-slate-700 font-medium">
                Una camicia su misura fa la differenza in ogni occasione.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 3: SOLUZIONE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                Una camicia{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-700 to-blue-600">
                  creata per il tuo corpo
                </span>
              </h2>
              
              <p className="text-xl text-slate-600 mb-12">
                Sarti italiani prendono le tue misure e realizzano una camicia che veste perfettamente.
                <br />
                <strong className="text-slate-800">Tu scegli tutto, loro cuciono.</strong>
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="w-8 h-8 text-slate-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Descrivi la tua camicia</h3>
                  <p className="text-slate-600">Tessuto, collo, polsino, colore, dettagli</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Ricevi proposte</h3>
                  <p className="text-slate-600">Sarti esperti ti contattano con preventivi</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Scegli il tuo sarto</h3>
                  <p className="text-slate-600">Nessun obbligo, decidi solo se ti convince</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: ESEMPI */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-4">
                Da idea a camicia perfetta
              </h2>
              <p className="text-xl text-slate-600 text-center mb-12">
                Ecco cosa hanno creato i nostri sarti
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                {SHIRT_EXAMPLES.map((example, idx) => (
                  <Card key={idx} className="overflow-hidden hover:shadow-xl transition-shadow">
                    <img 
                      src={example.image} 
                      alt={example.result}
                      className="w-full h-48 object-cover"
                    />
                    <div className="p-5">
                      <span className="text-xs font-medium text-blue-600 uppercase tracking-wider">
                        {example.category}
                      </span>
                      <p className="text-sm text-slate-500 mt-2 mb-1">
                        Richiesta: "{example.idea}"
                      </p>
                      <p className="font-semibold text-slate-800">
                        → {example.result}
                      </p>
                    </div>
                  </Card>
                ))}
              </div>
              
              <div className="text-center mt-10">
                <Button 
                  onClick={scrollToForm}
                  variant="outline"
                  className="border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Vuoi qualcosa di simile? Descrivi la tua idea
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 5: COME FUNZIONA */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50/50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                In 4 semplici passaggi
              </h2>
              
              <div className="space-y-8">
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-slate-700 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Descrivi la camicia ideale</h3>
                    <p className="text-slate-600">
                      Racconta cosa cerchi: tessuto, colore, tipo di collo e polsino, occasione d'uso. Allega foto di riferimento se le hai.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-slate-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi proposte dai sarti</h3>
                    <p className="text-slate-600">
                      Sarti specializzati vedono la tua richiesta e ti inviano proposte con tessuti, preventivi e tempi di realizzazione.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Fornisci le tue misure</h3>
                    <p className="text-slate-600">
                      Il sarto ti guiderà nel prendere le misure o potrai inviargli una camicia che ti veste bene come riferimento.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-6">
                  <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-xl flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Ricevi la tua camicia</h3>
                    <p className="text-slate-600">
                      Il sarto realizza la camicia e te la spedisce. Vestibilità perfetta garantita.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6: PERCHÉ FUNZIONA */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Perché scegliere una camicia su misura
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                  <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Vestibilità perfetta</h3>
                    <p className="text-sm text-slate-600">Ogni misura cucita sul tuo corpo</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-blue-50">
                  <Sparkles className="w-8 h-8 text-blue-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Tessuti pregiati</h3>
                    <p className="text-sm text-slate-600">Cotone, lino, seta italiani</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-50">
                  <Scissors className="w-8 h-8 text-slate-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Personalizzazione totale</h3>
                    <p className="text-sm text-slate-600">Collo, polsino, monogramma, bottoni</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 p-4 rounded-xl bg-green-50">
                  <Zap className="w-8 h-8 text-green-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold">Zero rischio</h3>
                    <p className="text-sm text-slate-600">Nessun obbligo di acquisto</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 7: TESTIMONIALS */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Cosa dicono i clienti
              </h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {TESTIMONIALS.map((testimonial, idx) => (
                  <Card key={idx} className="p-6 bg-white">
                    <div className="flex gap-1 mb-4">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className="w-5 h-5 fill-slate-700 text-slate-700" />
                      ))}
                    </div>
                    <p className="text-slate-600 mb-4 italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-semibold text-slate-600">
                        {testimonial.author[0]}
                      </div>
                      <div>
                        <p className="font-medium">{testimonial.author}</p>
                        <p className="text-slate-500 text-xs">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 8: FAQ */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
                Domande frequenti
              </h2>
              
              <div className="space-y-4">
                {FAQ_ITEMS.map((faq, idx) => (
                  <div 
                    key={idx}
                    className="border border-slate-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`shirt-faq-${idx}`}
                    >
                      <span className="font-medium text-slate-800">{faq.question}</span>
                      <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} />
                    </button>
                    {expandedFaq === idx && (
                      <div className="px-5 pb-5 text-slate-600">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 9: CTA FINALE */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-slate-100 to-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Shirt className="w-16 h-16 text-slate-600 mx-auto mb-6" />
              
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Pronto per la tua camicia su misura?
              </h2>
              
              <p className="text-xl text-slate-600 mb-8">
                I sarti italiani aspettano la tua richiesta.
              </p>
              
              <Button
                onClick={scrollToForm}
                size="lg"
                className="py-6 px-10 text-lg font-semibold bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white shadow-lg shadow-slate-200"
                data-testid="shirt-cta-final-button"
              >
                <Shirt className="w-5 h-5 mr-2" />
                Ricevi proposte gratis ora
              </Button>
            </div>
          </div>
        </section>

      </div>

      {/* Auth Prompt Modal */}
      {showAuthPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Accedi per continuare</h3>
            <p className="text-slate-600 mb-6">
              Per inviare la tua richiesta e ricevere proposte dai sarti, devi prima accedere o creare un account.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAuthPrompt(false)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={() => navigate('/login?redirect=/camicie-su-misura')}
                className="flex-1 bg-slate-700 hover:bg-slate-800"
              >
                Accedi
              </Button>
            </div>
          </Card>
        </div>
      )}
    </>
  );
}
